// File: src/pages/Trades.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { Link, useLocation } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { isNative } from "../iap";
import {
  toUTCDate,
  pickBuyPrice,
} from "../utils/smartTimingImpact";

import { getVerifiedWindowComparison, normalizedWindowKey } from "../utils/windowCloseComparison";

import {
  groupTradesByWindow,
  fmtUsd2,
  fmtSats0,
  fmtPrice0,
  fmtWindowLabel,
} from "../utils/tradeWindows";

// Capacitor Share (native share sheet)
import { Share } from "@capacitor/share";

// ──────────────────────────────────────────────────────────────
// Hook: true when viewport ≤ 640px (Tailwind "sm")
// ──────────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 640px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return isMobile;
};

// ──────────────────────────────────────────────────────────────
// Time helpers
// ──────────────────────────────────────────────────────────────
const userTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const dtfLocal = new Intl.DateTimeFormat(undefined, {
  timeZone: userTZ,
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

const MARKETING_TOTAL_RETURN_PCT = 18.41;

const formatSignedPct = (pct) => {
  if (!Number.isFinite(pct)) return "—";
  return `${pct > 0 ? "+" : ""}${pct.toFixed(2)}%`;
};

const formatSignedSats = (sats) => {
  if (!Number.isFinite(sats)) return "—";
  const rounded = Math.round(sats);
  return `${rounded > 0 ? "+" : ""}${rounded.toLocaleString()}`;
};

// ──────────────────────────────────────────────────────────────
// Share URL helper
// - Avoid localhost in shared message (always share prod download link on localhost)
// - Keep referral attribution via ?ref=
// ──────────────────────────────────────────────────────────────
const PROD_DOWNLOAD = "https://satstacker.app/download";

const buildShareUrl = (userInfo) => {
  const code = userInfo?.ref_code || userInfo?.referral_code || userInfo?.id || "";
  const origin = window.location.origin || "";
  const base =
    origin.includes("localhost") || origin.includes("127.0.0.1")
      ? PROD_DOWNLOAD
      : `${origin}/download`;

  return `${base}?ref=${encodeURIComponent(code)}`;
};

const buildTradeShareText = (t, comparison) => {
  const signupLine =
    `Use my link and you'll get 2 months of Smart Timing free instead of the usual 1.`;

  const buyPx = pickBuyPrice(t);
  const scheduledPrice = comparison?.scheduledPrice;
  const entryPct =
    Number.isFinite(scheduledPrice) && scheduledPrice > 0 &&
    Number.isFinite(buyPx) && buyPx > 0
      ? ((scheduledPrice - buyPx) / scheduledPrice) * 100
      : null;

  // Only share a positive, completed close-price comparison for the trade.
  if (t.smart_timing && entryPct != null && entryPct >= 0.005) {
    return (
      `Smart Timing bought Bitcoin ${entryPct.toFixed(2)}% below ` +
      `my scheduled DCA price ⚡\n` +
      signupLine
    );
  }

  return (
    `I'm automating Bitcoin DCA with SatStacker ⚡\n` +
    `Smart Timing looks for entries within each scheduled buy window.\n` +
    signupLine
  );
};

// ──────────────────────────────────────────────────────────────
// Mobile trade card
// ──────────────────────────────────────────────────────────────
const TradeCard = ({
  t, onShareTrade, shareBusy, priceComparison,
}) => {
  const btcAmt = parseFloat(t.btc_amt || 0);
  const sats = Math.floor(btcAmt * 1e8).toLocaleString();

  const grossUSD = parseFloat(t.usd_cost || 0);
  const feeUSD = parseFloat(t.fee || 0);
  const buyPx = pickBuyPrice(t);
  const price = Number.isFinite(buyPx) ? fmtPrice0(buyPx) : "—";
  const scheduledPrice = priceComparison?.scheduledPrice;
  const versusScheduledPct =
    Number.isFinite(scheduledPrice) && scheduledPrice > 0 &&
    Number.isFinite(buyPx) && buyPx > 0
      ? ((scheduledPrice - buyPx) / scheduledPrice) * 100
      : null;
  const showPositivePill =
    t.smart_timing && versusScheduledPct != null && versusScheduledPct >= 0.005;

  return (
    <div className="border border-border rounded-2xl px-3 py-2 bg-zinc-950/40 overflow-hidden">
      {/* Row 1: Date + actions */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-white font-semibold truncate min-w-0">
          {dtfLocal.format(toUTCDate(t.executed_at))}
        </p>

        <div className="shrink-0 flex items-center gap-2">
          {t.txid ? (
            <a
              href={`https://mempool.space/tx/${t.txid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs underline text-brand hover:opacity-90"
            >
              Tx
            </a>
          ) : null}
          <button
            type="button"
            onClick={() => onShareTrade(t)}
            disabled={shareBusy}
            aria-label="Share this buy"
            title="Share this buy"
            className={[
              "inline-flex items-center justify-center",
              "rounded-md border border-border bg-zinc-950/40",
              "p-1.5 text-white/80 hover:text-white hover:bg-zinc-900/60",
              "active:scale-[0.98] transition",
              shareBusy ? "opacity-60 cursor-not-allowed" : "",
            ].join(" ")}
          >
            <ExternalLink className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Row 2: 2x2 compact metrics (tighten spacing a touch) */}
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs tabular-nums">
        <div className="flex items-center justify-between rounded-lg border border-border bg-zinc-950/40 px-2 py-1.5">
          <span className="text-textMuted">Sats</span>
          <span className="text-white font-semibold">{sats}</span>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border bg-zinc-950/40 px-2 py-1.5">
          <span className="text-textMuted">USD</span>
          <span className="text-white font-semibold">{fmtUsd2(grossUSD)}</span>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border bg-zinc-950/40 px-2 py-1.5">
          <span className="text-textMuted">Px</span>
          <span className="text-white font-semibold">
            {price === "—" ? "—" : `$${price}`}
          </span>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border bg-zinc-950/40 px-2 py-1.5">
          <span className="text-textMuted">Fee</span>
          <span className="text-white font-semibold">{fmtUsd2(feeUSD)}</span>
        </div>
      </div>

      {/* Only completed buys that beat their scheduled close price get a pill. */}
      {showPositivePill && (
        <div className="mt-2">
          <span className="inline-flex items-center rounded-lg border border-border bg-zinc-950/40 px-2 py-1 text-[11px] font-semibold text-white tabular-nums">
            {versusScheduledPct.toFixed(2)}% below scheduled DCA
          </span>
        </div>
      )}
    </div>
  );
};

// A missing backend result after an expected deadline is not an open window.
// Do not leave old daily windows labelled "Pending" indefinitely.
const windowResultState = (w, performanceLoaded, performanceError) => {
  if (!w.isSmartTiming) return "standard";
  const start = toUTCDate(w.windowStart).getTime();
  const end = w.windowEnd ? toUTCDate(w.windowEnd).getTime() : NaN;
  const now = Date.now();

  // The window is still open regardless of API loading/errors or missing
  // close-price quotes. It must NEVER be called an unavailable comparison.
  if (Number.isFinite(end) && now < end) return "pending";
  if (w.priceComparison) return "complete";
  if (!performanceLoaded) return "loading";
  if (w.performanceResult || performanceError) return "unavailable";

  // Price collection can lag the scheduled deadline by a few minutes.
  if (Number.isFinite(end)) {
    return now < end + 20 * 60000 ? "finalizing" : "unavailable";
  }
  if (Number.isFinite(start) && now > start + 14 * 86400000 + 3600000) {
    return "unavailable";
  }
  return "pending"; // No safe historical end inferred yet.
};

const WINDOW_STATUS_LABELS = {
  standard: "—",
  loading: "Loading…",
  pending: "Not completed yet",
  finalizing: "Finalizing…",
  unavailable: "Comparison unavailable",
};

const WindowTradeCard = ({
  w, onShareTrade, shareBusy, performanceLoaded, performanceError,
}) => {
  const [open, setOpen] = useState(false);
  const comparison = w.priceComparison || null;
  const state = windowResultState(w, performanceLoaded, performanceError);

  return (
    <div className="border border-border rounded-2xl bg-zinc-950/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 text-left"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-bold text-white">{w.label}</p>
            <p className="text-xs text-textMuted mt-0.5">
              {w.executionLabel}
              {comparison
                ? ` · ${formatSignedSats(comparison.deltaSats)} sats vs scheduled DCA`
                : w.isSmartTiming
                  ? ` · ${state === "unavailable" ? "comparison unavailable" : state === "loading" ? "loading comparison" : state === "finalizing" ? "finalizing comparison" : "window still open"}`
                  : ""}
            </p>
          </div>

          <div className="shrink-0 text-right">
            <div className="text-sm font-bold text-white tabular-nums">
              {fmtUsd2(w.totalUsd)}
            </div>
            <div className="text-xs text-textMuted tabular-nums">
              {fmtSats0(w.totalSats)} sats
            </div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg border border-border bg-zinc-950/50 px-3 py-2">
            <div className="text-textMuted">Window result</div>
            <div className="mt-0.5 font-semibold tabular-nums text-white">
              {comparison
                ? `${formatSignedPct(comparison.gainPct)} vs scheduled DCA`
                : WINDOW_STATUS_LABELS[state]}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-zinc-950/50 px-3 py-2">
            <div className="text-textMuted">Avg buy price</div>
            <div className="mt-0.5 text-white font-semibold tabular-nums">
              {w.avgBuyPrice ? `$${fmtPrice0(w.avgBuyPrice)}` : "—"}
            </div>
          </div>
        </div>

        {comparison && (
          <div className="mt-2 text-[11px] text-textMuted">
            Scheduled DCA price: ${fmtPrice0(comparison.scheduledPrice)}
          </div>
        )}

        <div className="mt-2 text-[11px] text-textMuted">
          {open ? "Hide buy details ↑" : "Show buy details ↓"}
        </div>
      </button>

      {open && (
        <div className="border-t border-border px-3 pb-3 space-y-2">
          {(state === "unavailable" || state === "finalizing") && (
            <p className="pt-3 text-xs text-textMuted">
              {state === "unavailable"
                ? "Scheduled DCA comparison unavailable."
                : "Waiting for the final scheduled DCA price."}
            </p>
          )}

          {w.trades.map((t) => (
            <TradeCard
              key={t.id || t.executed_at}
              t={t}
              onShareTrade={(trade) => onShareTrade(trade, comparison)}
              shareBusy={shareBusy}
              priceComparison={comparison}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function Trades({ userInfo }) {
  const isMobile = useIsMobile();
  const location = useLocation();

  const loginTo = `/login?redirect=${encodeURIComponent(location.pathname)}`;

  const isReviewer = String(userInfo?.id) === "31";
  const forceMarketingSummary = isReviewer;

  const [trades, setTrades] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);

  const [currentPrice, setCurrentPrice] = useState(null);
  const [smartTimingPerformance, setSmartTimingPerformance] = useState(null);
  const [smartTimingPerformanceLoaded, setSmartTimingPerformanceLoaded] =
    useState(false);
  const [smartTimingPerformanceError, setSmartTimingPerformanceError] =
    useState(false);

  // Tiny status line (only used when we fall back to clipboard on web)
  const [shareBusy, setShareBusy] = useState(false);
  const [shareMsg, setShareMsg] = useState("");

  const shareUrl = useMemo(() => buildShareUrl(userInfo), [userInfo]);


  // Load current BTC price (for stats)
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const { data } = await api.get("/price");
        setCurrentPrice(parseFloat(data.btc_usd));
      } catch (err) {
        console.error("Failed to fetch BTC price from API", err);
      }
    };
    fetchPrice();
  }, []);

  // Load trades once we know the user ID
  useEffect(() => {
    if (!userInfo?.id) {
      setLoading(false);
      return;
    }
    fetchTrades();
    fetchSmartTimingPerformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.id]);

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trades");

      const sorted = (data || []).sort(
        (a, b) => toUTCDate(b.executed_at) - toUTCDate(a.executed_at)
      );

      setTrades(sorted);

      if (sorted.length) {
        localStorage.setItem("cachedTrades", JSON.stringify(sorted));
      }
    } catch (err) {
      console.error("Fetch trades failed:", {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
        url: err?.config?.baseURL + err?.config?.url,
      });
      setShareMsg(
        err?.response?.data?.detail ||
        `Failed to load trades (${err?.response?.status || "no status"})`
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchSmartTimingPerformance = async () => {
    setSmartTimingPerformanceLoaded(false);
    setSmartTimingPerformanceError(false);

    try {
      const { data } = await api.get(
        "/smart-timing/performance"
      );

      setSmartTimingPerformance(data || null);
    } catch (err) {
      console.error(
        "Fetch Smart Timing performance failed",
        err
      );
      setSmartTimingPerformance(null);
      setSmartTimingPerformanceError(true);
    } finally {
      setSmartTimingPerformanceLoaded(true);
    }
  };

  const INITIAL_COUNT = isMobile ? 3 : 4;

  const smartTimingWindowResults = useMemo(() => {
    const map = new Map();

    const windows =
      smartTimingPerformance?.windows ||
      smartTimingPerformance?.recent_windows ||
      [];

    for (const result of windows) {
      map.set(
        normalizedWindowKey(
          result?.plan_id,
          result?.window_start
        ),
        result
      );
    }

    return map;
  }, [smartTimingPerformance]);

  // Canonical aggregate Smart Timing result from the same completed-window
  // endpoint used by the Dashboard. Open windows are excluded by the backend.
  const smartTimingSummary =
    smartTimingPerformance?.benchmark === "scheduled_window_end"
      ? (smartTimingPerformance?.summary || null)
      : null;

  const groupedWindows = useMemo(() => {
    return groupTradesByWindow(trades).map((w) => {
      const performanceResult =
        smartTimingWindowResults.get(
          normalizedWindowKey(
            w?.planId,
            w?.windowStart
          )
        ) || null;

      // Use the backend's scheduled close quote after consistency checks,
      // but recalculate the price comparison from this page's trades.
      const priceComparison = getVerifiedWindowComparison(w, performanceResult);
      const windowEnd = performanceResult?.completed_at || w.windowEnd || null;
      return {
        ...w,
        label: windowEnd ? fmtWindowLabel(w.windowStart, windowEnd) : w.label,
        windowEnd,
        performanceResult,
        priceComparison,
      };
    });
  }, [
    trades,
    smartTimingWindowResults,
  ]);

  const displayedWindows = useMemo(
    () => (showAll ? groupedWindows : groupedWindows.slice(0, INITIAL_COUNT)),
    [showAll, groupedWindows, INITIAL_COUNT]
  );

  // Portfolio stats. Smart Timing performance comes from the backend's
  // canonical completed-window scheduled-DCA summary.
  const stats = useMemo(() => {
    if (!trades?.length || !Number.isFinite(currentPrice)) return null;

    let totalBTC = 0;
    let costBasisUSD = 0;

    for (const t of trades) {
      const btcAmt = parseFloat(t.btc_amt || 0);
      if (!Number.isFinite(btcAmt) || btcAmt <= 0) continue;

      const buyPx = pickBuyPrice(t);
      if (!Number.isFinite(buyPx) || buyPx <= 0) continue;

      totalBTC += btcAmt;
      costBasisUSD += btcAmt * buyPx;
    }

    const portfolioUSD = totalBTC * currentPrice;
    const totalReturnPct =
      costBasisUSD > 0
        ? ((portfolioUSD - costBasisUSD) / costBasisUSD) * 100
        : 0;

    return {
      totalTrades: trades.length,
      totalBTC,
      totalReturnPct,
      smartTimingGainPct:
        Number.isFinite(Number(smartTimingSummary?.gain_pct)) &&
        Number(smartTimingSummary?.completed_windows || 0) > 0
          ? Number(smartTimingSummary.gain_pct)
          : null,
      smartTimingCompletedWindows: Number(
        smartTimingSummary?.completed_windows || 0
      ),
      costBasisUSD,
    };
  }, [
    trades,
    currentPrice,
    smartTimingSummary,
  ]);

  const displayStats = useMemo(() => {
    if (!stats) return null;

    if (forceMarketingSummary) {
      return {
        ...stats,
        totalReturnPct: MARKETING_TOTAL_RETURN_PCT,
      };
    }

    return stats;
  }, [stats, forceMarketingSummary]);

  // ───────────────────────────────────────────────────────────
  // Share trade → native share sheet (Capacitor), else web share
  // ───────────────────────────────────────────────────────────
  const copyToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (!ok) throw new Error("Copy failed");
    return true;
  };

  const handleShareTrade = async (trade, priceComparison = null) => {
    if (shareBusy) return;
    setShareMsg("");
    setShareBusy(true);

    const title = "SatStacker";
    const text = buildTradeShareText(trade, priceComparison);

    try {
      // Native app: open iOS/Android share sheet (Messages is an option there)
      if (isNative()) {
        await Share.share({
          title: "SatStacker",
          text,
          url: shareUrl,
        });
        return;
      }

      // Mobile web supports navigator.share (also opens share sheet)
      if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl });
        return;
      }

      // Last resort: clipboard
      await copyToClipboard(`${text}\n${shareUrl}`);
      setShareMsg("Copied share text — paste it anywhere to share.");
    } catch (err) {
      const msg = String(err?.message || "");
      const isCancel =
        msg.toLowerCase().includes("abort") ||
        msg.toLowerCase().includes("canceled") ||
        msg.toLowerCase().includes("cancelled");

      if (!isCancel) {
        console.error("Trade share failed:", err);
        setShareMsg("Couldn’t share right now.");
      }
    } finally {
      setShareBusy(false);
    }
  };

  // ───────────────────────────────────────────────────────────
  // Logged out state
  // ───────────────────────────────────────────────────────────
  if (!userInfo?.id) {
    return (
      <div className="p-3 sm:p-6">
        <div className="max-w-2xl mx-auto ss-card ss-card-pad">
          <h1 className="ss-h1">My BTC Purchases</h1>
          <p className="mt-2 text-sm text-textMuted">
            You need an account to view your trade history.
          </p>

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Link to="/signup" className="ss-btn-primary px-5 py-3 text-center">
              Sign up
            </Link>
            <Link
              to={loginTo}
              className="ss-btn-secondary px-5 py-3 text-center"
            >
              Log in
            </Link>
          </div>

          <p className="mt-4 text-xs text-textMuted/80">
            Tip: after you connect Strike and your first buy runs, your history
            will appear here.
          </p>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────
  // Logged in view
  // ───────────────────────────────────────────────────────────
  return (
    <div className="p-3 sm:p-6">
      <div className="max-w-5xl mx-auto flex flex-col gap-4">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <div>
            <h1 className="ss-h1">My BTC Purchases</h1>
            <p className="text-sm text-textMuted mt-0.5">
              A ledger of your executed buys.
            </p>
          </div>

          {shareMsg ? (
            <div className="border border-border bg-zinc-950/40 rounded-xl px-3 py-2 text-sm text-white">
              {shareMsg}
            </div>
          ) : null}
        </div>

        {/* Table / Cards */}
        <div className="ss-card ss-card-pad mb-2 sm:mb-4">
          {loading ? (
            <div className="py-10 text-center text-textMuted text-sm">
              Loading…
            </div>
          ) : trades.length === 0 ? (
            <div className="py-10 text-center text-textMuted text-sm">
              No trades yet.
            </div>
          ) : isMobile ? (
            <div className="flex flex-col gap-3">
              {displayedWindows.map((w) => (
                <WindowTradeCard
                  key={w.key}
                  w={w}
                  onShareTrade={handleShareTrade}
                  shareBusy={shareBusy}
                  performanceLoaded={smartTimingPerformanceLoaded}
                  performanceError={smartTimingPerformanceError}
                />
              ))}
            </div>
          ) : (
            <div
              className="overflow-x-auto"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <table className="w-full min-w-[720px] text-sm tabular-nums">
                <thead className="ss-thead">
                  <tr>
                    <th className="p-3 text-left font-medium">Window</th>
                    <th className="p-3 text-left font-medium">Execution</th>
                    <th className="p-3 text-right font-medium">Sats</th>
                    <th className="p-3 text-right font-medium">USD</th>
                    <th className="p-3 text-right font-medium">Avg price</th>
                    <th className="p-3 text-right font-medium">Window result</th>
                  </tr>
                </thead>

                <tbody>
                  {displayedWindows.map((w) => (
                    <tr key={w.key} className="border-b border-border">
                      <td className="p-3 text-left text-white font-semibold whitespace-nowrap">
                        {w.label}
                      </td>

                      <td className="p-3 text-left text-textMuted">
                        {w.executionLabel}
                      </td>

                      <td className="p-3 text-right text-white">
                        {fmtSats0(w.totalSats)}
                      </td>

                      <td className="p-3 text-right text-white">
                        {fmtUsd2(w.totalUsd)}
                      </td>

                      <td className="p-3 text-right text-white">
                        {w.avgBuyPrice ? `$${fmtPrice0(w.avgBuyPrice)}` : "—"}
                      </td>

                      <td className="p-3 text-right text-white">
                        {w.priceComparison ? (
                          <>
                            <div className="text-white font-semibold">
                              {formatSignedPct(w.priceComparison.gainPct)} vs scheduled DCA
                            </div>
                            <div className="text-xs text-textMuted">
                              {formatSignedSats(w.priceComparison.deltaSats)} sats · Scheduled price: ${fmtPrice0(w.priceComparison.scheduledPrice)}
                            </div>
                          </>
                        ) : WINDOW_STATUS_LABELS[windowResultState(
                          w,
                          smartTimingPerformanceLoaded,
                          smartTimingPerformanceError
                        )]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Show more / less */}
          {groupedWindows.length > INITIAL_COUNT && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="w-full sm:w-auto rounded-xl border border-border bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 font-semibold transition"
              >
                {showAll ? "Show less" : "Show more"}
              </button>
            </div>
          )}
        </div>

        {/* Stats */}
        {displayStats && (
          <div className="ss-card ss-card-pad mb-2 sm:mb-4">
            <p className="text-sm font-semibold text-white mb-3">
              Summary
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center justify-between border border-border rounded-xl px-4 py-3 bg-zinc-950/40">
                <span className="text-textMuted">Total trades</span>
                <span className="text-white tabular-nums font-semibold">
                  {displayStats.totalTrades}
                </span>
              </div>

              <div className="flex items-center justify-between border border-border rounded-xl px-4 py-3 bg-zinc-950/40">
                <span className="text-textMuted">Total BTC accumulated</span>
                <span className="text-white tabular-nums font-semibold">
                  {displayStats.totalBTC.toFixed(6)} BTC
                </span>
              </div>

              <div className="flex items-center justify-between border border-border rounded-xl px-4 py-3 bg-zinc-950/40">
                <span className="text-textMuted">Total return</span>
                <span className="text-white tabular-nums font-semibold">
                  {displayStats.totalReturnPct.toFixed(2)}%
                </span>
              </div>

              {trades.some((t) => t.smart_timing) && (
                <div className="flex items-center justify-between border border-border rounded-xl px-4 py-3 bg-zinc-950/40">
                  <span className="text-textMuted">
                    Smart Timing vs scheduled DCA
                  </span>
                  <span className="text-white tabular-nums font-semibold">
                    {displayStats.smartTimingGainPct != null
                      ? formatSignedPct(displayStats.smartTimingGainPct)
                      : smartTimingPerformanceLoaded
                        ? "Pending"
                        : "Loading…"}
                  </span>
                </div>
              )}
            </div>

            {trades.some((t) => t.smart_timing) && (
              <p className="mt-3 text-xs text-textMuted/80">
                Completed-window results use fee-adjusted sats vs scheduled DCA at the closing price; the benchmark assumes a 0.99% Strike fee.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}